import { useEffect, useRef, useState } from "react";
import { Check, Loader2, TriangleAlert, type LucideIcon } from "lucide-react";
import type { RequestState } from "@redux/state/shared-types";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";

const PhantomLabel = ({ icon: Icon, label }: { icon?: LucideIcon; label: string }) => (
  <span aria-hidden="true" className="invisible col-start-1 row-start-1 inline-flex items-center gap-2 whitespace-nowrap">
    {Icon && <Icon className="size-4" />}
    {label}
  </span>
);

interface Props {
  processing: RequestState;
  isDirty: boolean;
  isSubmitting: boolean;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  successLabel?: string;
  submittingLabel?: string;
  errorLabel?: string;
}

const FEEDBACK_DISPLAY_DURATION_MS = 2000;

export const FormSaveActions = ({
  processing,
  isDirty,
  isSubmitting,
  onCancel,
  submitLabel = "Enregistrer",
  cancelLabel = "Annuler",
  successLabel = "Enregistré",
  submittingLabel = "Enregistrement…",
  errorLabel = "Échec",
}: Props) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const prevIsDirty = useRef(isDirty);

  useEffect(() => {
    if (processing === "success") {
      setShowSuccess(true);
      setShowError(false);
    } else if (processing === "failed") {
      setShowError(true);
      setShowSuccess(false);
    }
  }, [processing]);

  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(() => setShowSuccess(false), FEEDBACK_DISPLAY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    if (!showError) return;
    const timer = setTimeout(() => setShowError(false), FEEDBACK_DISPLAY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showError]);

  useEffect(() => {
    const userJustEdited = !prevIsDirty.current && isDirty;
    prevIsDirty.current = isDirty;
    if (!userJustEdited) return;
    if (showSuccess) setShowSuccess(false);
    if (showError) setShowError(false);
  }, [isDirty, showSuccess, showError]);

  const isLoading = isSubmitting || processing === "pending";
  const disabled = !isDirty || isLoading || showSuccess;
  const variant = showSuccess ? "success" : showError ? "destructive" : "default";

  return (
    <div className="flex items-center justify-end gap-1">
      {onCancel && (
        <Button type="button" variant="ghost" onClick={onCancel} disabled={disabled}>
          {cancelLabel}
        </Button>
      )}
      <Button
        type="submit"
        disabled={disabled}
        variant={variant}
        className={cn("transition-colors duration-200", showSuccess && "disabled:opacity-100")}
        aria-live="polite"
      >
        <span className="inline-grid place-items-center">
          <PhantomLabel icon={Loader2} label={submittingLabel} />
          <PhantomLabel icon={Check} label={successLabel} />
          <PhantomLabel icon={TriangleAlert} label={errorLabel} />
          <PhantomLabel label={submitLabel} />
          <span className="col-start-1 row-start-1 inline-flex items-center justify-center gap-2 whitespace-nowrap">
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                {submittingLabel}
              </>
            ) : showSuccess ? (
              <span className="inline-flex items-center gap-2 duration-300 animate-in fade-in zoom-in-95">
                <Check className="size-4" aria-hidden="true" />
                {successLabel}
              </span>
            ) : showError ? (
              <span className="inline-flex items-center gap-2 duration-300 animate-in fade-in zoom-in-95">
                <TriangleAlert className="size-4" aria-hidden="true" />
                {errorLabel}
              </span>
            ) : (
              submitLabel
            )}
          </span>
        </span>
      </Button>
    </div>
  );
};
